// components/UserPage/PackagingOrderDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import { Package, Box as BoxIcon, ShoppingCart, Thermometer, MapPin, Droplets, Plus, Minus } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';

export const PackagingOrderDialog = ({ 
  open, 
  onClose, 
  items = []
}) => {
  const { isDark } = useTheme();
  
  const [quantities, setQuantities] = useState({
    cardboard: 0,
    plastic: 0
  });
  
  const [extraMaterials, setExtraMaterials] = useState({
    thermalSensor: 0,
    fillers: 0,
    locationSensor: 0
  });
  
  const [error, setError] = useState(null);

  const packagingOptions = [
    {
      id: 'cardboard',
      name: 'Cardboard Boxes',
      description: 'Eco-friendly, recyclable cardboard boxes',
      price: 0.50,
      icon: <BoxIcon size={40} />,
      color: '#8B4513',
      specs: '30×20×15cm • Max 5kg',
      presets: [1, 5, 10, 30, 100]
    },
    {
      id: 'plastic',
      name: 'Plastic Containers',
      description: 'Durable, reusable plastic containers with lids',
      price: 0.75,
      icon: <Package size={40} />,
      color: '#2196f3',
      specs: 'Ø12cm × 20cm • Screw-lock',
      presets: [1, 5, 10, 30, 100]
    }
  ];

  const extraMaterialsOptions = [
    {
      id: 'thermalSensor',
      name: 'Thermal Sensor',
      description: 'Monitor temperature during storage',
      price: 2.50,
      icon: <Thermometer size={24} />,
      color: '#FF5722'
    },
    {
      id: 'fillers',
      name: 'Protective Fillers',
      description: 'Bubble wrap, foam, packing peanuts',
      price: 0.30,
      icon: <Droplets size={24} />,
      color: '#4CAF50'
    },
    {
      id: 'locationSensor',
      name: 'Location Sensor',
      description: 'Track package location in real-time',
      price: 3.00,
      icon: <MapPin size={24} />,
      color: '#9C27B0'
    }
  ];

  const handleQuantityChange = (type, value) => {
    const num = parseInt(value) || 0;
    setQuantities(prev => ({
      ...prev,
      [type]: Math.max(0, num)
    }));
    setError(null);
  };

  const handlePresetClick = (type, preset) => {
    setQuantities(prev => ({
      ...prev,
      [type]: preset
    }));
    setError(null);
  };

  const handleExtraMaterialChange = (type, delta) => {
    setExtraMaterials(prev => ({
      ...prev,
      [type]: Math.max(0, prev[type] + delta)
    }));
  };

  const calculateTotal = () => {
    let total = 0;
    
    // Main packaging
    packagingOptions.forEach(option => {
      total += option.price * (quantities[option.id] || 0);
    });
    
    // Extra materials
    extraMaterialsOptions.forEach(option => {
      total += option.price * (extraMaterials[option.id] || 0);
    });
    
    return total;
  };

  const handleOrder = () => {
    const totalItems = quantities.cardboard + quantities.plastic;
    const totalExtras = Object.values(extraMaterials).reduce((sum, val) => sum + val, 0);
    
    if (totalItems === 0 && totalExtras === 0) {
      setError('Please select at least one item');
      return;
    }

    // TODO: Implement actual order logic
    console.log('Ordering packaging:', {
      items: items,
      quantities,
      extraMaterials,
      total: calculateTotal()
    });

    // Close dialog and show success message
    onClose();
    alert(`Order placed successfully! Total: €${calculateTotal().toFixed(2)}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart size={24} />
          Order Packaging Materials
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          For {items.length} {items.length === 1 ? 'item' : 'items'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Main Packaging Options */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Packaging
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {packagingOptions.map((option) => (
            <Grid item xs={12} key={option.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderColor: quantities[option.id] > 0 ? option.color : 'divider',
                  borderWidth: quantities[option.id] > 0 ? 2 : 1,
                  bgcolor: quantities[option.id] > 0 
                    ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.50')
                    : 'background.paper',
                  transition: 'all 0.2s',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Icon */}
                    <Box 
                      sx={{ 
                        color: option.color,
                        display: 'flex',
                        alignItems: 'flex-start'
                      }}
                    >
                      {option.icon}
                    </Box>

                    {/* Details */}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                        <Typography variant="h6" color={option.color} sx={{ fontWeight: 700 }}>
                          €{option.price.toFixed(2)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {option.specs}
                      </Typography>
                      
                      {/* Quantity Presets */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {option.presets.map((preset) => (
                          <Chip
                            key={preset}
                            label={preset}
                            onClick={() => handlePresetClick(option.id, preset)}
                            color={quantities[option.id] === preset ? 'primary' : 'default'}
                            variant={quantities[option.id] === preset ? 'filled' : 'outlined'}
                            sx={{ 
                              fontWeight: 600,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: isDark ? 'rgba(72, 202, 228, 0.15)' : 'primary.50',
                              }
                            }}
                          />
                        ))}
                      </Box>

                      {/* Custom Quantity Input */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                          Custom:
                        </Typography>
                        <TextField
                          type="number"
                          value={quantities[option.id]}
                          onChange={(e) => handleQuantityChange(option.id, e.target.value)}
                          InputProps={{
                            inputProps: { min: 0, max: 10000 }
                          }}
                          sx={{ width: 120 }}
                          size="small"
                        />
                        {quantities[option.id] > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            = €{(option.price * quantities[option.id]).toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Extra Materials */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Extra Materials
        </Typography>
        
        <Grid container spacing={2}>
          {extraMaterialsOptions.map((option) => (
            <Grid item xs={12} sm={6} key={option.id}>
              <Card 
                variant="outlined"
                sx={{
                  borderColor: extraMaterials[option.id] > 0 ? option.color : 'divider',
                  borderWidth: extraMaterials[option.id] > 0 ? 2 : 1,
                  bgcolor: extraMaterials[option.id] > 0 
                    ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.50')
                    : 'background.paper',
                  transition: 'all 0.2s',
                  height: '100%'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                    <Box sx={{ color: option.color, mt: 0.5 }}>
                      {option.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {option.description}
                      </Typography>
                      <Typography variant="body2" color={option.color} sx={{ fontWeight: 700 }}>
                        €{option.price.toFixed(2)} / unit
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Quantity Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <IconButton 
                      size="small"
                      onClick={() => handleExtraMaterialChange(option.id, -1)}
                      disabled={extraMaterials[option.id] === 0}
                      sx={{ 
                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'grey.200',
                        }
                      }}
                    >
                      <Minus size={16} />
                    </IconButton>
                    
                    <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center', fontWeight: 600 }}>
                      {extraMaterials[option.id]}
                    </Typography>
                    
                    <IconButton 
                      size="small"
                      onClick={() => handleExtraMaterialChange(option.id, 1)}
                      sx={{ 
                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'grey.200',
                        }
                      }}
                    >
                      <Plus size={16} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Order Summary */}
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.50', 
          borderRadius: 1 
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Order Summary
          </Typography>
          <Divider sx={{ my: 1 }} />
          
          {/* Packaging Items */}
          {packagingOptions.map((option) => (
            quantities[option.id] > 0 && (
              <Box 
                key={option.id} 
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="body2">
                  {option.name} × {quantities[option.id]}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  €{(option.price * quantities[option.id]).toFixed(2)}
                </Typography>
              </Box>
            )
          ))}
          
          {/* Extra Materials */}
          {extraMaterialsOptions.map((option) => (
            extraMaterials[option.id] > 0 && (
              <Box 
                key={option.id} 
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="body2">
                  {option.name} × {extraMaterials[option.id]}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  €{(option.price * extraMaterials[option.id]).toFixed(2)}
                </Typography>
              </Box>
            )
          ))}
          
          {calculateTotal() === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
              No items selected
            </Typography>
          )}
          
          <Divider sx={{ my: 1 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Total
            </Typography>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              €{calculateTotal().toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleOrder}
          disabled={calculateTotal() === 0}
          sx={{ textTransform: 'none' }}
        >
          Place Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};